function safeParseJson(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function findTargetForms(root) {
  const productId = String(root.dataset.productId || '');
  const explicitSelector = root.dataset.formSelector?.trim();
  const candidates = [];

  if (explicitSelector) {
    const nearestScope =
      root.closest('product-form-component, quick-add-component, .product-card, .product-details') || document;
    const scoped = nearestScope.querySelector(explicitSelector);
    if (scoped) candidates.push(scoped);

    for (const node of document.querySelectorAll(explicitSelector)) {
      if (!candidates.includes(node)) candidates.push(node);
    }
  }

  if (candidates.length === 0) {
    const fallback =
      root.closest("form[action='/cart/add']") ||
      root.closest('product-form-component')?.querySelector("form[action='/cart/add']") ||
      root.closest('quick-add-component')?.querySelector("form[action='/cart/add']") ||
      root.closest('.product-card')?.querySelector("form[action='/cart/add']") ||
      document.querySelector("form[action='/cart/add']");
    if (fallback) candidates.push(fallback);
  }

  const forms = [];
  for (const node of candidates) {
    if (!(node instanceof HTMLFormElement)) continue;

    const formProductId = String(node.closest('product-form-component')?.dataset?.productId || '');
    if (productId && formProductId && formProductId !== productId) continue;
    if (!forms.includes(node)) forms.push(node);
  }

  return forms;
}

function ensureSellingPlanInput(form) {
  if (!form) return null;
  let input = form.querySelector("input[name='selling_plan'][data-stayai-clone-source='true']");
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.dataset.stayaiCloneSource = 'true';
    form.appendChild(input);
  }
  return input;
}

function setPriceMarkup(node, compareAtPrice, price, options = {}) {
  if (!node) return;
  const preserveCompareIfEmpty = options.preserveCompareIfEmpty === true;
  const existingCompare = node.querySelector('s')?.textContent?.trim() || '';
  const nextCompare = compareAtPrice || (preserveCompareIfEmpty ? existingCompare : '');

  node.textContent = '';
  if (nextCompare) {
    const compareNode = document.createElement('s');
    compareNode.textContent = nextCompare;
    node.appendChild(compareNode);
    node.appendChild(document.createTextNode(' '));
  }
  node.appendChild(document.createTextNode(price || ''));
}

function parseMoneyParts(text) {
  if (!text) return null;
  const match = text.match(/([^\d\s])\s?(\d[\d,]*(?:\.\d{2})?)/);
  if (!match) return null;
  const amount = Number.parseFloat(match[2].replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;
  return { symbol: match[1], amount };
}

function formatMoney(amount, symbol) {
  if (!Number.isFinite(amount)) return '';
  return `${symbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getSelectedVariantPerServingParts() {
  const checkedLabel = document
    .querySelector(".variant-option__button-label input[type='radio']:checked")
    ?.closest('.variant-option__button-label');
  const text = checkedLabel?.textContent || '';
  const match = text.match(/([^\d\s]\s?\d[\d,]*(?:\.\d{2})?)\s*per\s*serving/i);
  if (!match) return null;
  return parseMoneyParts(match[1]);
}

/**
 * @param {string} sizeSetting Blank or "all" matches every size; "none" matches none.
 * @param {{ title?: string } | null | undefined} variantState
 * @param {number | null} servings
 * @returns {boolean}
 */
function kitItemMatchesSize(sizeSetting, variantState, servings) {
  const raw = (sizeSetting || '').trim();
  if (!raw || raw.toLowerCase() === 'all') return true;
  if (raw.toLowerCase() === 'none') return false;

  const title = (variantState?.title || '').toLowerCase();
  const tokens = raw
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

  return tokens.some((token) => {
    const tokenServings = Number.parseFloat(token.replace(/servings?/gi, '').trim());
    const tokenIsServingsNumber = Number.isFinite(tokenServings) && /^[\d.]+(\s*servings?)?$/.test(token);

    if (tokenIsServingsNumber) {
      return servings != null && tokenServings === servings;
    }

    return title.includes(token);
  });
}

/**
 * @param {HTMLElement} root
 * @param {{ title?: string } | null | undefined} variantState
 * @returns {void}
 */
function setWelcomeKitVisibility(root, variantState) {
  const kit = root.querySelector('[data-stayai-welcome-kit]');
  if (!kit) return;

  const servings = getServingsCount(root, variantState);
  const items = kit.querySelectorAll('[data-stayai-kit-item]');
  let visibleCount = 0;

  for (const item of items) {
    const matches = kitItemMatchesSize(item.dataset.stayaiKitSizes || '', variantState, servings);
    item.hidden = !matches;
    if (matches) visibleCount += 1;
  }

  kit.hidden = visibleCount === 0;
}

function getServingsCount(root, variantState) {
  const fromTitle = variantState?.title?.match(/(\d+(?:\.\d+)?)\s*servings?/i);
  if (fromTitle) {
    const servings = Number.parseFloat(fromTitle[1]);
    if (Number.isFinite(servings) && servings > 0) return servings;
  }

  const fromVariantLabel =
    document
      .querySelector(".variant-option__button-label input[type='radio']:checked")
      ?.closest('.variant-option__button-label')
      ?.textContent?.match(/(\d+(?:\.\d+)?)\s*servings?/i) || null;
  if (fromVariantLabel) {
    const servings = Number.parseFloat(fromVariantLabel[1]);
    if (Number.isFinite(servings) && servings > 0) return servings;
  }

  const fallback = Number.parseFloat(root.dataset.fallbackServings || '');
  if (Number.isFinite(fallback) && fallback > 0) return fallback;

  return null;
}

function getSavePercent(variantState, selectedPlan) {
  if (Number.isFinite(selectedPlan?.save_percent)) {
    return selectedPlan.save_percent;
  }

  if (Number.isFinite(variantState?.save_percent)) {
    return variantState.save_percent;
  }

  const compareCents =
    variantState?.one_time_compare_at_cents > variantState?.one_time_price_cents
      ? variantState.one_time_compare_at_cents
      : variantState?.one_time_price_cents;
  const subscriptionCents = selectedPlan?.price_cents;

  if (!compareCents || !subscriptionCents || subscriptionCents >= compareCents) {
    return 0;
  }

  return Math.round(((compareCents - subscriptionCents) / compareCents) * 100);
}

/**
 * @param {HTMLElement} root
 * @param {{ title?: string, save_percent?: number } | null | undefined} variantState
 * @param {{ save_percent?: number } | null | undefined} selectedPlan
 * @returns {void}
 */
function setSaveCopy(root, variantState, selectedPlan) {
  const header = root.querySelector('[data-stayai-save-header]');
  const prefixEl = root.querySelector('[data-stayai-best-value-prefix]');
  const saveEl = root.querySelector('[data-stayai-save-percent]');
  const detailEl = root.querySelector('[data-stayai-save-detail]');
  const savePercent = getSavePercent(variantState, selectedPlan);
  const headerTemplate = root.dataset.i18nSavePercent || 'Save __P__%';
  const detailTemplate = root.dataset.i18nSaveDetail || 'Save __P__% on each order';
  const servings = getServingsCount(root, variantState);
  const showPrefix = kitItemMatchesSize(root.dataset.bestValueOnSize || '', variantState, servings);

  if (prefixEl) prefixEl.hidden = !showPrefix;

  if (savePercent > 0) {
    if (header) header.hidden = false;
    if (saveEl) saveEl.textContent = headerTemplate.replace('__P__', String(savePercent));
    if (detailEl) detailEl.textContent = detailTemplate.replace('__P__', String(savePercent));
    return;
  }

  if (header) header.hidden = true;
}

function setSubtexts(root, variantState, selectedPlan) {
  const oneTimeEl = root.querySelector('[data-stayai-onetime-subtext]');
  const subscriptionEl = root.querySelector('[data-stayai-subscription-subtext]');
  const servings = getServingsCount(root, variantState);
  const servingWord =
    servings === 1
      ? root.dataset.i18nServing || 'serving'
      : root.dataset.i18nServings || 'servings';
  const servingsLabel = servings ? `${servings} ${servingWord}` : '';

  if (oneTimeEl) {
    oneTimeEl.textContent = servingsLabel;
  }

  if (subscriptionEl) {
    const planName = (selectedPlan?.name || '').replace(/^Delivered\s+/i, '').trim();
    subscriptionEl.textContent = [servingsLabel, planName].filter(Boolean).join(' ');
  }
}

function getPlanDropdown(planSelect) {
  return planSelect?.closest('[data-stayai-plan-dropdown]') || null;
}

function closePlanDropdown(dropdown) {
  if (!dropdown) return;

  const button = dropdown.querySelector('[data-stayai-plan-dropdown-button]');
  const list = dropdown.querySelector('[data-stayai-plan-dropdown-list]');
  dropdown.classList.remove('is-open');
  if (button) button.setAttribute('aria-expanded', 'false');
  if (list) list.hidden = true;
}

function syncPlanDropdown(planSelect) {
  const dropdown = getPlanDropdown(planSelect);
  if (!dropdown) return;

  const label = dropdown.querySelector('[data-stayai-plan-dropdown-label]');
  const list = dropdown.querySelector('[data-stayai-plan-dropdown-list]');
  if (!label || !list) return;

  const selected = planSelect.selectedOptions[0];
  label.textContent = selected?.textContent?.trim() || '';
  list.textContent = '';

  for (const option of planSelect.options) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'stayai-plan-dropdown__option';
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
    item.dataset.value = option.value;
    item.textContent = option.textContent;
    if (option.selected) item.classList.add('is-selected');
    list.appendChild(item);
  }
}

function setPlanSelectVisibility(planSelect, plans) {
  if (!planSelect) return;

  const visible = Boolean(plans && plans.length > 1);
  const dropdown = getPlanDropdown(planSelect);
  if (dropdown) dropdown.hidden = !visible;
}

function initPlanDropdown(planSelect) {
  const dropdown = getPlanDropdown(planSelect);
  if (!dropdown || dropdown.dataset.stayaiPlanDropdownReady === 'true') return;

  const button = dropdown.querySelector('[data-stayai-plan-dropdown-button]');
  const list = dropdown.querySelector('[data-stayai-plan-dropdown-list]');
  if (!button || !list) return;

  dropdown.dataset.stayaiPlanDropdownReady = 'true';

  const toggle = () => {
    if (dropdown.classList.contains('is-open')) {
      closePlanDropdown(dropdown);
      return;
    }

    dropdown.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    list.hidden = false;
  };

  button.addEventListener('click', (event) => {
    event.stopPropagation();
    toggle();
  });

  list.addEventListener('click', (event) => {
    const option = event.target.closest('[data-value]');
    if (!option) return;

    event.preventDefault();
    event.stopPropagation();
    planSelect.value = option.dataset.value;
    planSelect.dispatchEvent(new Event('change', { bubbles: true }));
    syncPlanDropdown(planSelect);
    closePlanDropdown(dropdown);
  });

  document.addEventListener('click', (event) => {
    if (!dropdown.contains(event.target)) closePlanDropdown(dropdown);
  });

  dropdown.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    closePlanDropdown(dropdown);
    button.focus();
  });

  syncPlanDropdown(planSelect);
}

function setServingTags(root, variantState, selectedPlan) {
  const oneTimeServingEl = root.querySelector('[data-stayai-onetime-serving]');
  const subscriptionServingEl = root.querySelector('[data-stayai-subscription-serving]');
  if (!oneTimeServingEl || !subscriptionServingEl) return;

  const servings = getServingsCount(root, variantState);
  const variantPerServing = getSelectedVariantPerServingParts();
  const oneTimeParts = parseMoneyParts(variantState?.one_time_price || '');
  const subscriptionParts = parseMoneyParts(selectedPlan?.price || '');

  if (variantPerServing) {
    oneTimeServingEl.textContent = `${formatMoney(variantPerServing.amount, variantPerServing.symbol)}/SERVING`;
  } else if (oneTimeParts && servings) {
    oneTimeServingEl.textContent = `${formatMoney(oneTimeParts.amount / servings, oneTimeParts.symbol)}/SERVING`;
  } else {
    oneTimeServingEl.textContent = '';
  }

  if (subscriptionParts && servings) {
    subscriptionServingEl.textContent = `${formatMoney(
      subscriptionParts.amount / servings,
      subscriptionParts.symbol
    )}/SERVING`;
  } else {
    subscriptionServingEl.textContent = '';
  }
}

/**
 * @param {string} selectedOption
 * @returns {void}
 */
function setQuantitySaveBadges(selectedOption) {
  const badges = document.querySelectorAll('[data-stayai-save-badge]');
  const useOneTime = selectedOption === 'onetime';

  for (const badge of badges) {
    const percent = useOneTime ? badge.dataset.saveOnetime : badge.dataset.saveSubscription;
    const template = badge.dataset.saveTemplate || 'Save __P__%';

    if (percent) {
      const amountEl = document.createElement('span');
      amountEl.className = 'variant-option__save-badge-amount';
      amountEl.textContent = percent;

      const parts = template.split('__P__');
      badge.hidden = false;
      badge.replaceChildren(document.createTextNode(parts[0] || ''), amountEl, document.createTextNode(parts[1] || ''));
    } else {
      badge.hidden = true;
    }
  }
}

function syncSelectionUi(root, selectedOption) {
  const labels = root.querySelectorAll('.stayai-purchase-label');

  for (const label of labels) {
    label.classList.toggle('is-selected', label.dataset.stayaiPurchaseOption === selectedOption);
  }

  setQuantitySaveBadges(selectedOption);
}

function initCloneWidget(root) {
  if (!(root instanceof HTMLElement) || root.dataset.stayaiCloneInitialized === 'true') return;
  root.dataset.stayaiCloneInitialized = 'true';

  const getTargetForms = () => findTargetForms(root);
  const forms = getTargetForms();
  const form = forms[0] || null;
  const oneTimeLabel = root.querySelector("[data-stayai-purchase-option='onetime']");
  const subscriptionLabel = root.querySelector("[data-stayai-purchase-option='subscription']");
  const oneTimeInput = oneTimeLabel?.querySelector("input[type='radio']");
  const subscriptionInput = subscriptionLabel?.querySelector("input[type='radio']");
  const oneTimePriceEl = root.querySelector('[data-stayai-onetime-price]');
  const subscriptionPriceEl = root.querySelector('[data-stayai-subscription-price]');
  const planSelect = root.querySelector('[data-stayai-plan-select]');
  const variantMapEl = root.querySelector('[data-stayai-variant-map]');
  const variantMap = safeParseJson(variantMapEl?.textContent || '');
  const fallbackVariantId = root.dataset.selectedVariantId || '';
  const formProductId = form?.closest('product-form-component')?.dataset?.productId || '';

  if (!oneTimeInput || !subscriptionInput || !planSelect || !variantMap) return;
  if (formProductId && String(formProductId) !== String(root.dataset.productId || '')) return;

  let selectedOption = subscriptionInput.checked ? 'subscription' : oneTimeInput.checked ? 'onetime' : null;

  const ensureCheckedState = () => {
    if (oneTimeInput.checked || subscriptionInput.checked) return;

    const shouldSelectSubscription =
      selectedOption === 'subscription' ||
      (selectedOption == null && root.dataset.defaultToSubscription === 'true');
    subscriptionInput.checked = shouldSelectSubscription;
    oneTimeInput.checked = !shouldSelectSubscription;
    selectedOption = subscriptionInput.checked ? 'subscription' : 'onetime';
  };

  ensureCheckedState();

  const getCurrentVariantId = () => {
    const fromForm = form?.querySelector("input[name='id'], select[name='id']")?.value || '';
    return fromForm || fallbackVariantId;
  };

  const applySellingPlanToInputs = (planId) => {
    const sellingPlanInputs = [];
    for (const targetForm of getTargetForms()) {
      const sellingPlanInput = ensureSellingPlanInput(targetForm);
      if (sellingPlanInput) sellingPlanInputs.push(sellingPlanInput);
    }

    for (const sellingPlanInput of sellingPlanInputs) {
      sellingPlanInput.value = planId || '';
      sellingPlanInput.disabled = !planId;
    }

    return sellingPlanInputs;
  };

  const syncSellingPlanToForm = () => {
    ensureCheckedState();
    const sellingPlanInputs = applySellingPlanToInputs(
      subscriptionInput.checked ? planSelect.value : ''
    );
    if (sellingPlanInputs.length === 0) {
      syncSelectionUi(root, subscriptionInput.checked ? 'subscription' : 'onetime');
      return;
    }
    if (!subscriptionInput.checked) {
      syncSelectionUi(root, 'onetime');
      selectedOption = 'onetime';
      return;
    }
    syncSelectionUi(root, 'subscription');
    selectedOption = 'subscription';
  };

  const renderVariantState = () => {
    const variantId = getCurrentVariantId();
    const variantState = variantMap[String(variantId)] || variantMap[String(fallbackVariantId)];
    const plans = variantState?.plans || [];

    root.hidden = false;
    setWelcomeKitVisibility(root, variantState);

    if (!variantState) {
      return;
    }

    if (plans.length === 0) {
      setPriceMarkup(oneTimePriceEl, variantState.one_time_compare_at_price, variantState.one_time_price);
      setPriceMarkup(subscriptionPriceEl, variantState.one_time_compare_at_price, variantState.one_time_price);
      setSubtexts(root, variantState, null);
      setServingTags(root, variantState, null);
      setSaveCopy(root, variantState, null);
      applySellingPlanToInputs('');
      return;
    }
    setPriceMarkup(oneTimePriceEl, variantState.one_time_compare_at_price, variantState.one_time_price);

    const currentPlanId = planSelect.value;
    const existingValues = Array.from(planSelect.options).map((option) => option.value);
    const nextValues = plans.map((plan) => String(plan.id));
    const optionsChanged =
      existingValues.length !== nextValues.length ||
      existingValues.some((value, index) => value !== nextValues[index]);

    if (optionsChanged) {
      planSelect.textContent = '';
      plans.forEach((plan) => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.name;
        planSelect.appendChild(option);
      });
    }

    const hasCurrent = plans.some((plan) => String(plan.id) === String(currentPlanId));
    planSelect.value = hasCurrent ? currentPlanId : String(plans[0].id);
    const selectedPlan = plans.find((plan) => String(plan.id) === String(planSelect.value)) || plans[0];
    setPriceMarkup(subscriptionPriceEl, selectedPlan?.compare_at_price, selectedPlan?.price);
    setSaveCopy(root, variantState, selectedPlan);
    setSubtexts(root, variantState, selectedPlan);
    setServingTags(root, variantState, selectedPlan);
    setPlanSelectVisibility(planSelect, plans);
    syncPlanDropdown(planSelect);
    syncSellingPlanToForm();
  };

  initPlanDropdown(planSelect);
  oneTimeInput.addEventListener('change', syncSellingPlanToForm);
  subscriptionInput.addEventListener('change', syncSellingPlanToForm);
  planSelect.addEventListener('change', () => {
    const variantId = getCurrentVariantId();
    const variantState = variantMap[String(variantId)] || variantMap[String(fallbackVariantId)];
    const selectedPlan = variantState?.plans?.find((plan) => String(plan.id) === String(planSelect.value)) || null;
    if (selectedPlan) {
      setPriceMarkup(subscriptionPriceEl, selectedPlan.compare_at_price, selectedPlan.price);
      setSaveCopy(root, variantState, selectedPlan);
      setSubtexts(root, variantState, selectedPlan);
      setServingTags(root, variantState, selectedPlan);
    }
    syncSellingPlanToForm();
  });

  const variantChanged = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches("input[name='id'], select[name='id']")) renderVariantState();
  };

  form?.addEventListener('input', variantChanged);
  form?.addEventListener('change', variantChanged);

  document.addEventListener('variant:update', (event) => {
    const detail = event?.detail || {};
    const eventProductId = String(detail?.data?.productId || '');
    if (!eventProductId || eventProductId !== String(root.dataset.productId || '')) return;

    const nextVariantId = detail?.resource?.id;
    if (nextVariantId) {
      for (const targetForm of getTargetForms()) {
        const variantIdInput = targetForm.querySelector("input[name='id'], select[name='id']");
        if (variantIdInput) variantIdInput.value = String(nextVariantId);
      }
    }
    renderVariantState();
  });

  // Guard against late async scripts that can unset both radios after initial paint.
  [150, 400, 900, 1800].forEach((delay) => {
    window.setTimeout(() => {
      ensureCheckedState();
      syncSellingPlanToForm();
    }, delay);
  });

  renderVariantState();
}

function initAllCloneWidgets() {
  document.querySelectorAll('[data-stayai-clone]').forEach((root) => initCloneWidget(root));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initAllCloneWidgets();
    const observer = new MutationObserver(initAllCloneWidgets);
    observer.observe(document.body, { childList: true, subtree: true });
  });
} else {
  initAllCloneWidgets();
  const observer = new MutationObserver(initAllCloneWidgets);
  observer.observe(document.body, { childList: true, subtree: true });
}
